import i18n from "i18next";
import { store } from "../store";

const PICKER_TIMEZONES = [
  ["Pacific/Honolulu", "US"],
  ["America/Anchorage", "US"],
  ["America/Los_Angeles", "US"],
  ["America/Phoenix", "US"],
  ["America/Denver", "US"],
  ["America/Chicago", "US"],
  ["America/New_York", "US"],
  ["America/Indiana/Knox", "US"],
  ["America/Indiana/Indianapolis", "US"],
  ["America/Regina", "CA"],
  ["America/Mexico_City", "MX"],
  ["Etc/GMT+5", ""],
  ["America/Halifax", "CA"],
  ["America/Montevideo", "UY"],
  ["America/Puerto_Rico", "PR"],
  ["America/Nuuk", "GL"],
  ["America/Sao_Paulo", "BR"],
  ["America/St_Johns", "CA"],
  ["America/Scoresbysund", "GL"],
  ["Atlantic/Reykjavik", "IS"],
  ["UTC", ""],
  ["Europe/London", "GB"],
  ["Europe/Lisbon", "PT"],
  ["Europe/Paris", "FR"],
  ["Europe/Berlin", "DE"],
  ["Europe/Athens", "GR"],
  ["Africa/Johannesburg", "ZA"],
  ["Africa/Nairobi", "KE"],
  ["Asia/Tehran", "IR"],
  ["Asia/Dubai", "AE"],
  ["Asia/Karachi", "PK"],
  ["Asia/Kolkata", "IN"],
  ["Asia/Kathmandu", "NP"],
  ["Asia/Dhaka", "BD"],
  ["Asia/Yangon", "MM"],
  ["Asia/Jakarta", "ID"],
  ["Asia/Bangkok", "TH"],
  ["Asia/Hong_Kong", "HK"],
  ["Asia/Singapore", "SG"],
  ["Australia/Perth", "AU"],
  ["Asia/Seoul", "KR"],
  ["Asia/Tokyo", "JP"],
  ["Australia/Darwin", "AU"],
  ["Australia/Adelaide", "AU"],
  ["Australia/Brisbane", "AU"],
  ["Australia/Sydney", "AU"],
  ["Pacific/Noumea", "NC"],
  ["Pacific/Norfolk", "NF"],
  ["Pacific/Tarawa", "KI"],
  ["Pacific/Auckland", "NZ"],
  ["Pacific/Apia", "WS"],
  ["Atlantic/South_Georgia", "GS"],
  ["Atlantic/Azores", "PT"],
  ["Atlantic/Cape_Verde", "CV"],
  ["Asia/Kabul", "AF"],
  ["Pacific/Chatham", "NZ"],
  ["Pacific/Kiritimati", "KI"]
];

const CIVIL_DATE_SEPARATOR = "-";
const CIVIL_DATE_STRING_LENGTH = 10;
const MILLIS_IN_DAY = 24 * 60 * 60 * 1000;
const CIVIL_DATE_ANCHOR_HOUR = 12;
const JANUARY = 0;
const JULY = 6;

const validTimezoneCache = {};
const wallClockFormatters = {};
const civilDateFormatters = {};
let displayTimezoneSelector = null;
let browserTimezone = null;
let browserTimezoneKey = null;

const getBrowserOffsetKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  return [now, new Date(year, JANUARY, 1), new Date(year, JULY, 1)].map(date => date.getTimezoneOffset()).join("|");
};

export const getBrowserTimezone = () => {
  const key = getBrowserOffsetKey();
  if (key !== browserTimezoneKey) {
    browserTimezoneKey = key;
    browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  return browserTimezone;
};

export const isValidTimezone = tz => {
  if (!tz === true) {
    return false;
  }
  if (!(tz in validTimezoneCache)) {
    try {
      new Intl.DateTimeFormat("en-US", { timeZone: tz });
      validTimezoneCache[tz] = true;
    } catch (e) {
      validTimezoneCache[tz] = false;
    }
  }
  return validTimezoneCache[tz];
};

export const resolveTimezone = tz => (isValidTimezone(tz) ? tz : getBrowserTimezone());

export const setDisplayTimezoneSelector = selector => {
  displayTimezoneSelector = selector;
};

export const getUserTimezone = () =>
  store && displayTimezoneSelector ? displayTimezoneSelector(store.getState()) : getBrowserTimezone();

const getTimezoneNamePart = (tz, timeZoneName, date = new Date()) => {
  const namePart = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName })
    .formatToParts(date)
    .find(part => part.type === "timeZoneName");
  return namePart ? namePart.value : "";
};

export const getTimezoneOffsetName = (tz, date = new Date()) => {
  const offsetName = getTimezoneNamePart(tz, "longOffset", date);
  return !offsetName === true || offsetName === "GMT" ? "GMT+00:00" : offsetName;
};

export const getUtcOffsetForTimezone = (tz, date = new Date()) => {
  const match = getTimezoneOffsetName(tz, date).match(/^GMT([+-])(\d{2}):(\d{2})$/);
  if (!match === true) {
    return 0;
  }
  const minutes = parseInt(match[2], 10) * 60 + parseInt(match[3], 10);
  return match[1] === "+" ? -minutes : minutes;
};

const getCivilDateFormatter = tz => {
  if (!civilDateFormatters[tz]) {
    civilDateFormatters[tz] = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    });
  }
  return civilDateFormatters[tz];
};

export const getCivilDateString = (instant = new Date(), tz = getUserTimezone()) => {
  const date = new Date(instant);
  if (isNaN(date.getTime())) {
    return null;
  }
  const parts = {};
  getCivilDateFormatter(resolveTimezone(tz))
    .formatToParts(date)
    .forEach(part => {
      parts[part.type] = part.value;
    });
  return [parts.year, parts.month, parts.day].join(CIVIL_DATE_SEPARATOR);
};

export const toCivilDateString = value => {
  if (!value === true) {
    return null;
  }
  return typeof value === "string" ? value.slice(0, CIVIL_DATE_STRING_LENGTH) : getCivilDateString(value);
};

export const getTodayCivilDateString = () => getCivilDateString();

export const parseCivilDateString = dateString => {
  const [year, month, day] = dateString.split(CIVIL_DATE_SEPARATOR).map(Number);
  return { year, month, day };
};

const toCivilUtcTime = dateString => {
  const { year, month, day } = parseCivilDateString(dateString);
  return Date.UTC(year, month - 1, day);
};

const shiftCivilDateString = (dateString, years, months, days) => {
  const civilDateString = toCivilDateString(dateString);
  if (!civilDateString === true) {
    return null;
  }
  const { year, month, day } = parseCivilDateString(civilDateString);
  return new Date(Date.UTC(year + years, month - 1 + months, day + days))
    .toISOString()
    .slice(0, CIVIL_DATE_STRING_LENGTH);
};

export const addDaysToCivilDateString = (dateString, days) => shiftCivilDateString(dateString, 0, 0, days);

export const addMonthsToCivilDateString = (dateString, months) => shiftCivilDateString(dateString, 0, months, 0);

export const addYearsToCivilDateString = (dateString, years) => shiftCivilDateString(dateString, years, 0, 0);

export const getCivilDaysBetween = (fromDateString, toDateString) => {
  const from = toCivilDateString(fromDateString);
  const to = toCivilDateString(toDateString);
  if (!from === true || !to === true) {
    return NaN;
  }
  return (toCivilUtcTime(to) - toCivilUtcTime(from)) / MILLIS_IN_DAY;
};

const getTimezoneCity = tz =>
  tz.includes("/")
    ? tz
        .split("/")
        .pop()
        .replace(/_/g, " ")
    : "";

const getCanonicalTimezone = tz => new Intl.DateTimeFormat("en-US", { timeZone: tz }).resolvedOptions().timeZone;

const getPickerTimezone = tz => {
  const canonicalTimezone = getCanonicalTimezone(tz);
  return PICKER_TIMEZONES.find(([pickerTimezone]) => getCanonicalTimezone(pickerTimezone) === canonicalTimezone);
};

const getCountryName = countryCode => {
  if (!countryCode === true || typeof Intl.DisplayNames !== "function") {
    return "";
  }
  return new Intl.DisplayNames(["en-US"], { type: "region" }).of(countryCode) || "";
};

const getTimezoneName = tz => {
  const pickerTimezone = getPickerTimezone(tz);
  if (pickerTimezone) {
    return i18n.t(`timezoneName.${pickerTimezone[0]}`);
  }
  const genericName = getTimezoneNamePart(tz, "longGeneric");
  const city = getTimezoneCity(tz);
  return city ? `${genericName} (${city})` : genericName;
};

export const getTimezoneLabel = tz => `(${getTimezoneOffsetName(tz)}) ${getTimezoneName(tz)}`;

export const getTimezoneOptions = currentTimezone => {
  const timezones = PICKER_TIMEZONES.filter(([tz]) => isValidTimezone(tz));
  if (isValidTimezone(currentTimezone) && !getPickerTimezone(currentTimezone) === true) {
    timezones.push([currentTimezone, ""]);
  }
  return timezones
    .map(([tz, countryCode]) => {
      const label = getTimezoneLabel(tz);
      return {
        tz,
        label,
        gmtOffset: -getUtcOffsetForTimezone(tz),
        searchText: `${label} ${getCountryName(countryCode)} ${tz.replace(/_/g, " ")}`.toLowerCase()
      };
    })
    .sort((a, b) => a.gmtOffset - b.gmtOffset);
};

const TIMEZONE_MATCH_RANK = {
  WHOLE_WORD: 0,
  WORD_PREFIX: 1,
  SUBSTRING: 2
};

const getTimezoneMatchRank = (searchText, query) => {
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  if (new RegExp(`(^|[^a-z0-9])${escapedQuery}($|[^a-z0-9])`).test(searchText)) {
    return TIMEZONE_MATCH_RANK.WHOLE_WORD;
  }
  if (new RegExp(`(^|[^a-z0-9])${escapedQuery}`).test(searchText)) {
    return TIMEZONE_MATCH_RANK.WORD_PREFIX;
  }
  return TIMEZONE_MATCH_RANK.SUBSTRING;
};

export const searchTimezoneOptions = (options, search) => {
  const query = (search || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");
  if (!query === true) {
    return options;
  }
  return options
    .filter(option => option.searchText.includes(query))
    .map(option => ({ option, rank: getTimezoneMatchRank(option.searchText, query) }))
    .sort((a, b) => a.rank - b.rank)
    .map(({ option }) => option);
};

const getWallClockFormatter = tz => {
  if (!wallClockFormatters[tz]) {
    wallClockFormatters[tz] = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric"
    });
  }
  return wallClockFormatters[tz];
};

export const getInstantForCivilDate = (dateString, tz = getUserTimezone()) => {
  const { year, month, day } = parseCivilDateString(dateString);
  const wallClockAsUtc = Date.UTC(year, month - 1, day, CIVIL_DATE_ANCHOR_HOUR);
  const formatter = getWallClockFormatter(resolveTimezone(tz));
  const offsetAt = instant => {
    const parts = {};
    formatter.formatToParts(new Date(instant)).forEach(part => {
      parts[part.type] = parseInt(part.value, 10);
    });
    return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second) - instant;
  };
  return wallClockAsUtc - offsetAt(wallClockAsUtc - offsetAt(wallClockAsUtc));
};

export const toUserTimezoneDate = (instant = new Date()) => {
  const date = new Date(instant);
  const tz = getUserTimezone();
  if (isNaN(date.getTime()) || tz === getBrowserTimezone()) {
    return date;
  }
  const parts = {};
  getWallClockFormatter(tz)
    .formatToParts(date)
    .forEach(part => {
      parts[part.type] = parseInt(part.value, 10);
    });
  return new Date(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    date.getMilliseconds()
  );
};

export const isTodayInUserTimezone = instant => {
  const date = toUserTimezoneDate(instant);
  const today = toUserTimezoneDate();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
};
